import { gsap, Power2, Back, Bounce } from 'gsap';
import { Point, BLEND_MODES, Texture } from "pixi.js";
import { SanEngineManager } from '../../engine/SanEngineManager';
import { SanSpriteSymbol } from "../../engine/Symbol/SanSpriteSymbol";
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { Sequence } from "../../../core/displayobject/Sequence";
import { Helper } from "../../../core/utils/Helper";
import { SanQuentin2 } from "../../SanQuentin2";
import { SoundConfig } from "../../config/sound/SoundConfig";
import { SoundManager } from "../../config/sound/SoundManager";
import { GameConfig } from "../../config/base/GameConfig";
import { SimpleSpriteSwap } from '../symbol/SimpleSpriteSwap';
import { SpinePlayer } from '../../../core/displayobject/SpinePlayer';
import { AnimationEffects } from '../../../core/effects/AnimationEffects';

export interface WildAnimationJump {
    mult?: number;
    reel: number;
    row: number;
    duration?: number;
}

export class JumpingWildSymbol extends PIXI.Container implements StageLayer {

    ID: number;
    ReelID: number;
    RowID: number;

    jw_Sprite_BG: SimpleSpriteSwap;

    jw_Spine: SpinePlayer;
    seq_burn_FX: Sequence;

    jw_mult: PIXI.Sprite;
    multValue: number = 1;

    burnSize: number = 0;

    timeline: gsap.core.Timeline;

    public get canSplit(): boolean {
        return !!this.seq_burn_FX;
    }

    public get linkedReelSymbol(): SanSpriteSymbol {
        return SanEngineManager.getReel(this.ReelID).getSymbol(this.RowID)!;
    }

    private get skinVal(): string {
        return this.multValue == 1 ? "jw" :"default";
    }

    constructor(_id: number, reel?: number, row?: number) {
        super();
        this.ID = _id;
        this.ReelID = reel ?? 0;
        this.RowID = row ?? 1;
        this.init();
    }

    init(): void {

        this.jw_Sprite_BG = this.addChild(new SimpleSpriteSwap(SanQuentin2.assetLoader.getTextures("W_J/4/JW_BG")[0]));
        this.jw_Sprite_BG.alpha = 0;

        this.jw_mult = new PIXI.Sprite();
        this.jw_mult = this.addChild(new PIXI.Sprite());
        this.jw_mult.anchor.set(0.5);

        // this.addChild(this.jw_Sprite);
        this.jw_Spine = this.addChild(new SpinePlayer("Jumping_Wild"));
        this.jw_Spine.play({animation:"idle",showOnEnd:true, skinType: this.skinVal});
        this.jw_Spine.visible = true;
        this.addChild(this.jw_mult);


        // skins: default, jw

        /*
        "appear",
        "idle",
        "jump_out",
        "win"
        */
       
        this.alpha = 0;
        this.sortableChildren = true;
    }

    public trySplit(symbolSizes: number[][]): gsap.core.Timeline {
        const splitTL = Helper.setTimeline();
        if (!this.canSplit) return splitTL;

        this.seq_burn_FX.stop();
        this.seq_burn_FX.destroy();
        this.seq_burn_FX = null!;

        const affectedReels = SanEngineManager.AllReels.filter(reel => reel.reelID != this.ReelID);
        affectedReels.forEach(reel => {
            const offset = Math.abs(this.ReelID - reel.reelID) * GameConfig.SPLIT_OFFSET;
            splitTL.add(reel.getSymbol(this.RowID)!.doSplit(this.multValue, true, symbolSizes[reel.reelID][this.RowID + 1]), offset)
        });
        splitTL.add(this.linkedReelSymbol.MarkedForExtraSplit(false), 0);
        this.multValue = symbolSizes[this.ReelID][this.RowID + 1];

        return splitTL;
    }

    public barJWUpgrade(): void {
        if (!this.seq_burn_FX) {
            this.seq_burn_FX = this.addChild(new Sequence(SanQuentin2.assetLoader.getTextures("Burning_Badge")));
            this.seq_burn_FX.anchor.set(0.5);
            this.seq_burn_FX.blendMode = BLEND_MODES.ADD;
            this.seq_burn_FX.play({ repeat: -1 });

            gsap.fromTo(this.seq_burn_FX.scale, { x: 1.2, y: 1.2 }, { x: 1, y: 1, duration: 0.15 });
            gsap.fromTo(this.seq_burn_FX, { alpha: 0 }, { alpha: 1, duration: 0.15 });
            this.burnSize = 1;
        }
        else if(this.burnSize){
            this.seq_burn_FX.setTexture("Burning_Badge_Big");
            this.seq_burn_FX.play({repeat:-1});
            this.burnSize = 2;
        }
    }

    public changeMult(multValue: number): void {
        if (!multValue || multValue <= 1 || multValue == this.multValue) return;

        if (multValue % 2) {
            console.error("Managed to mult with odd number");
            multValue = 2;
        }

        this.jw_mult.texture = SanQuentin2.assetLoader.getTextures("JW_Multiplier/X_" + multValue)[0];
        this.multValue = multValue;
    }

    public JumpTo(conf: WildAnimationJump, noShake: boolean = false, noSound: boolean = false): gsap.core.Timeline {

        this.ReelID = conf.reel;
        this.RowID = conf.row;

        var toPosition: Point = this.fetchPos();

        this.timeline = Helper.setTimeline(this.timeline!,{onComplete:() => this.resize()});

        if (this.ID == 0 && !noSound) {
            this.timeline.add(() => { SoundManager.playSoundEffect(SoundConfig.FS_JUMPINGWILD_MOVE) });
        }

        this.timeline.to(this.scale, { y: SanEngineManager.dim.scale.y, duration: 0.3 / 2 }, 0);

        const e: any = Back.easeIn.config(2.43);

        this.jw_Spine.play({animation:"idle", showOnEnd:true, skinType: this.skinVal});

        this.timeline.to(this,
            {
                x: toPosition.x, y: toPosition.y,
                ease: e, duration: conf.duration ? conf.duration : 0.3
            }
        );
        this.timeline.add(() => {
            this.jw_Sprite_BG.alpha = 0.6;
             this.jw_Spine.play({animation:"jump_out",showOnEnd:true, skinType: this.skinVal});
             if(this.jw_mult) AnimationEffects.Bounce(this.jw_mult);
        });

        return this.timeline;
    }

    public jumpDelay(): number {
        return this.timeline ? this.timeline!.duration() - this.timeline!.time() : 0;
    }

    private playIdleLoop(seq: Sequence): void {
        seq.alpha = 0;
        seq.play({ onCompleteCallback: () => this.playIdleLoop(seq), onStart: () => seq.alpha = 1 }).delay(2 + Math.random() * 4)
    }

    public onSpinStart(symVal: number): void {
        this.changeMult(symVal);
        this.alpha = 1;

        this.jw_Spine.play({animation:"idle",showOnEnd:true, skinType: this.skinVal});
        // this.jw_Spine.alpha = 1;
        // gsap.fromTo(this.jw_Spine,{alpha:0},{alpha:1,duration:0.5});
        this.position = Helper.copyLocalPosition(this, this.linkedReelSymbol);
    }

    public onSpinStop(durrLeft: number): void {
        this.linkedReelSymbol?.SetJw(this.multValue, durrLeft, this.burnSize);

        if (this.canSplit) {
            this.linkedReelSymbol.jwBurnConsent(this.seq_burn_FX);
            this.seq_burn_FX = null!;
            this.burnSize = 0;
        }
        this.alpha = 0;
        this.jw_Sprite_BG.alpha = 0;
    }

    public remove(): void {
        this.children.forEach(child => { child.destroy(); });
        this.destroy({ children: true });
    }

    private fetchPos(): PIXI.Point {
        return Helper.copyLocalPosition(this, SanQuentin2.reelEngineManager.getSymSPPoint(this.ReelID, this.RowID));
    }

    public resize(): void {
        this.scale = SanEngineManager.dim.scale;
        this.position = this.fetchPos();
    }
}